import { safe, unwrap } from "pb.safe";
import { useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import {
	POSConnectorClient,
	type Payload,
} from "../../../@lib-pos-connector-client";
import { logger } from "../../../shared/logger";
import { never } from "../../../shared/never";
import * as transport from "../../../transport";
import { createDone } from "../shared/create-done";
import { DeclineResponse_fromRefundPosConnectorResponse } from "../shared/declined-response";
import { getPageProps } from "../shared/get-declined-page-props";
import { createOperationCopy } from "../shared/operation-pages";
import {
	AcknowledgedResponse,
	CancelRequest,
	createCloseOnSilence,
	createCloseHandlers,
	createErrorPages,
	createMessageGuard,
	createReconnector,
	createRequestIdGuard,
	type POSConnectorFrame,
	STATE_INITIAL_TIMEOUT,
	STATE_TRANSACTION_STATE_TIMEOUT,
} from "../shared/pos-connector-session";
import { useApiClient } from "../shared/use-api-client";
import { useHealthcheck } from "../shared/use-healthcheck";
import type { RefundUnmatchedRequest } from "../terminal-refund";
import { Page } from "./refund-unmatched/page";
import { render } from "./refund-unmatched/render";
import { getApprovedResponse } from "./shared/get-approved-response";
import {
	RefundAcknowledgedSchema,
	RefundResponseSchema,
	RefundSignatureVerificationSchema,
	RefundTerminalBusySchema,
	RefundTransactionEventSchema,
	type RefundResponse,
} from "./shared/schemas";

const resolve = transport.Resolve<"Terminal_refund">();

/** The pages a silent socket can settle on; `done` needs the `onClose` key. */
type ClosablePage = Extract<Page, { onClose: () => void }>;

export namespace RefundUnmatched {
	export type Props = {
		request: RefundUnmatchedRequest;
	};
}

export function RefundUnmatched(props: RefundUnmatched.Props) {
	const apiClient = useApiClient();
	const healthcheck = useHealthcheck();

	const { request } = props;

	const [sessionId] = useState(() => request.query.sessionUuid ?? uuidv4());
	// Correlates every frame in one attempt; a retry mints a fresh id so stale
	// frames from the previous attempt are ignored.
	const requestIdRef = useRef(uuidv4());

	const [renderedPage, setRenderedPage] = useState<Page>(() =>
		Page.Initialising(),
	);
	const setPage = useCallback(
		(nextOrFn: Page | ((prev: Page) => Page)) => {
			// not supported in refunds  at present but keep for future support
			if (!request.interactive) {
				return undefined;
			}
			return void setRenderedPage((prev) => {
				const next = typeof nextOrFn === "function" ? nextOrFn(prev) : nextOrFn;
				return next;
			});
		},
		[request.interactive],
	);

	const RefundUnmatchedRequest = useCallback(() => {
		const now = new Date();
		return {
			action: "refund",
			eventId: uuidv4(),
			requestId: requestIdRef.current,
			timestamp: now.getTime().toString(),
			data: {
				// Currently required by backend.
				transactionUuid: uuidv4(),
				sessionUuid: sessionId,
				amount: request.query.amount,
				externalReference: request.query.reference ?? "",
				isCardPresent: true,
				terminalContact: request.query.terminalContact,
				terminalNote: request.query.terminalNote,
				terminalReceipt: request.query.terminalReceipt,
				terminalReceiptPrint: request.query.terminalReceiptPrint,
			},
		} satisfies Payload;
	}, [request, sessionId]);

	useEffect(() => {
		const matchesRequestId = createRequestIdGuard(() => requestIdRef.current);

		transport.onCancel(() => {
			logger.info("refund: unmatched external cancel called");

			return void done(
				Page.Cancelled({ onClose: closeWithCancelled(copy.Cancelled) }),
				"onClose",
				{ skip: true },
			);
		});

		let _timeout: ReturnType<typeof setTimeout> | undefined = undefined;
		let _connection: POSConnectorClient | undefined = undefined;

		function dispose() {
			transport.onCancel(undefined);
			if (_timeout) _timeout = void clearTimeout(_timeout);
			if (_connection) _connection = void _connection.close();
		}

		function updateTimeout(callback: () => void, delay: number) {
			clearTimeout(_timeout);
			_timeout = setTimeout(callback, delay);
		}

		const done = createDone<Page>({
			dispose,
			setPage,
			interactive: request.interactive,
		});

		const _deviceId = request.headers.deviceId;
		if (!_deviceId) {
			return void (() =>
				done(
					Page.ErrorOperation({
						onClose: () =>
							void resolve.Error("Setup Required", {
								message: "Not configured.",
							}),
					}),
					"onClose",
				))();
		}

		const deviceId = _deviceId;

		main();
		function main() {
			const onRetry = () => main();
			const pages = createErrorPages<ClosablePage>({
				Page,
				copy,
				close: {
					closeWithConnectionFailure,
					closeWithInactivityTimeout,
					closeWithNetworkFailure,
				},
				onRetry,
			});

			// Each attempt (initial or retry) gets a fresh requestId.
			requestIdRef.current = uuidv4();

			done.isLocked = false;
			setPage(Page.Initialising());

			// Prevent continuation if token resolve _after_ timeout.
			if (done.isLocked) {
				return;
			}

			let _hasRequested = false;
			// Survives a reconnect, which re-registers the host cancel handler.
			let _processing = false;

			const reconnector = createReconnector({
				deviceId,
				healthcheck,
				updateTimeout,
				connect: () => void connect(),
				onExhausted: () => void done(pages.disconnected(), "onClose"),
				onNetworkError: () => void done(pages.network(), "onClose"),
			});

			const handle = createMessageGuard({
				onUnreachable: () => void done(pages.terminalUnreachable(), "onClose"),
				onForbidden: () => void done(pages.terminalMissing(), "onClose"),
				onOperationError: () => void done(pages.operation(), "onClose"),
			});

			void connect();
			async function connect() {
				logger.debug("refund: unmatched connect", {
					attempt: reconnector.attempt,
				});

				updateTimeout(() => {
					return void done(pages.network(), "onClose");
				}, STATE_INITIAL_TIMEOUT);

				const authToken = (await request.tokens())?.authAccessToken;

				// Prevent continuation if token resolve _after_ timeout.
				if (done.isLocked) {
					return;
				}

				if (!authToken) {
					return void (() =>
						done(
							Page.ErrorOperation({
								onClose: () =>
									void resolve.Error("Setup Required", {
										message: "Not authenticated.",
									}),
							}),
							"onClose",
						))();
				}

				function onExternalCancel() {
					logger.info("refund: unmatched external cancel called");

					if (!connection.ready) {
						return void done(
							Page.Cancelled({ onClose: closeWithCancelled(copy.Cancelled) }),
							"onClose",
							{ skip: true },
						);
					}

					return void cancel(true);
				}

				transport.onCancel(
					_processing ? transport.onCancel.ignoreExternal : onExternalCancel,
				);

				const connection = POSConnectorClient({
					authToken,
					deviceUuid: deviceId,
					onOpen,
					onClose: reconnector.onClose,
					onError: reconnector.onError,
				});
				_connection = connection;

				function onOpen() {
					logger.debug("refund: unmatched open");

					if (!_hasRequested) {
						begin();
					} else {
						listen();
					}
				}

				const closeOnSilence = createCloseOnSilence<ClosablePage>({
					connection,
					deviceId,
					healthcheck,
					sessionId,
					done,
					isLocked,
					timer: () => _timeout,
					network: pages.network,
					disconnected: pages.disconnected,
				});

				function isLocked() {
					return done.isLocked;
				}

				// Silence before the terminal acknowledges is never the merchant
				// idling — they have not been shown a prompt yet. A live socket
				// means the terminal itself never picked the request up.
				function onSilentRequest() {
					void closeOnSilence(pages.terminalUnreachable, onRetry);
				}

				function onSilentTransaction() {
					void closeOnSilence(pages.inactivity, onRetry);
				}

				function inactivity() {
					updateTimeout(onSilentTransaction, STATE_TRANSACTION_STATE_TIMEOUT);
				}

				function begin() {
					const payload = RefundUnmatchedRequest();

					updateTimeout(onSilentRequest, STATE_INITIAL_TIMEOUT);

					connection.onMessage(
						handle((message) => {
							{
								// This message is deprecated in favour of
								// `RefundResponseSchema`'s `data.responseCode` of
								// "Q9".
								const event = RefundTerminalBusySchema.safeParse(message).data;
								if (event) {
									return void (() => {
										return void done(
											Page.ErrorTerminalBusy({
												onClose: closeWithPairedDeviceBusy(
													copy.ErrorTerminalBusy,
												),
												onRetry,
											}),
											"onClose",
										);
									})();
								}
							}
							{
								const event = unwrap(
									safe(() => RefundAcknowledgedSchema.parse(message)),
								);
								if (event) {
									if (
										!matchesRequestId(event.requestId, {
											sessionUuid: event.data.sessionUuid,
										})
									) {
										return;
									}
									// The terminal has the refund; AWAITING_PAYMENT_METHOD
									// moves off this page.
									setPage(Page.InitialisingRefund());
									return void listen();
								}
							}
							// The terminal's opening ACKNOWLEDGED can be lost when its socket
							// rotates. Any frame carrying this attempt's requestId proves the
							// session is live, so open it and replay the frame.
							if (
								message.requestId &&
								matchesRequestId(message.requestId, { action: message.action })
							) {
								logger.warn("refund: unmatched frame before acknowledge");
								setPage(Page.InitialisingRefund());
								return void listen()(message);
							}
						}),
					);

					connection.send(payload);
					_hasRequested = true;
				}

				function listen() {
					inactivity();

					function next(page: Page) {
						// The terminal can't honour a cancel while it is processing, so drop
						// the host's request; the screen has no close control here either.
						_processing = page.$type === "AwaitProcessing";
						transport.onCancel(
							_processing
								? transport.onCancel.ignoreExternal
								: onExternalCancel,
						);
						setPage(page);
						// Reset reconnection attempts when we successfully receive data.
						reconnector.reset();
						// Reset inactivity timeout on every navigation.
						inactivity();
					}

					const onFrame = (message: POSConnectorFrame) => {
						{
							// This message is deprecated in favour of
							// `PurchaseResponseSchema`'s `data.status` of "TERMINAL_BUSY".
							// This branch and `PurchaseTerminalBusySchema` can be removed
							// once backend notifies us of its upstream removal.
							const event = RefundTerminalBusySchema.safeParse(message).data;
							if (event) {
								return void (() =>
									done(
										Page.ErrorTerminalBusy({
											onClose: closeWithPairedDeviceBusy(
												copy.ErrorTerminalBusy,
											),
											onRetry,
										}),
										"onClose",
									))();
							}
						}
						{
							const event = RefundResponseSchema.safeParse(message).data;
							if (event) {
								return void (() => {
									if (
										!matchesRequestId(event.requestId, {
											status: event.data.status,
										})
									) {
										return;
									}

									connection.send(
										AcknowledgedResponse({
											action: "refund",
											sessionId,
											requestId: requestIdRef.current,
										}),
									);

									if (event.data.status === "APPROVED") {
										return void approved(event);
									} else {
										return void declined(event);
									}
								})();
							}
						}
						{
							const event =
								RefundTransactionEventSchema.safeParse(message).data;
							if (event) {
								return void (() => {
									resolve.Event(event.data.event, {
										message: event.data.displayMessage,
									});

									switch (event.data.event) {
										case "AWAITING_ACCOUNT_SELECTION":
											return next(
												Page.AwaitAccountSelection({
													onClose: cancel,
												}),
											);
										case "AWAITING_APPLICATION_SELECTION":
											return next(
												Page.AwaitApplicationSelection({
													onClose: cancel,
												}),
											);
										case "AWAITING_PAYMENT_METHOD":
											return next(
												Page.AwaitPaymentMethod({
													onClose: cancel,
												}),
											);
										case "AWAITING_PIN_ENTRY":
											return next(
												Page.AwaitPinEntry({
													onClose: cancel,
												}),
											);
										case "PROCESSING":
											return next(Page.AwaitProcessing());
										case "DISPLAYING_PAYMENT_MESSAGE":
										case "RETRYING_PAYMENT_METHOD":
											return next(
												Page.DisplayPaymentMessage({
													onClose: cancel,
												}),
											);
										default:
											logger.debug(
												"refund: unmatched transaction event unhandled variant",
											);
											return never(event.data.event);
									}
								})();
							}
						}
						{
							const event =
								RefundSignatureVerificationSchema.safeParse(message)?.data;
							if (event) {
								return void (() => {
									if (!event.requestId) {
										logger.warn("refund: signature missing requestId", {
											state: event.data.state,
										});
									}

									resolve.Event(event.data.state, {
										message: event.data.signature,
									});

									switch (event.data.state) {
										case "AWAITING_SIGNATURE":
										case "SIGNATURE_CLEARED":
											return next(
												Page.AwaitSignature({
													onClose: cancel,
												}),
											);
										case "SIGNATURE_SUBMITTED": {
											const sendSignatureVerificationEvent = (
												isValid: boolean,
											) => {
												connection.send({
													action: "signatureVerification",
													eventId: uuidv4(),
													requestId: event.requestId,
													timestamp: Date.now().toString(),
													data: {
														event: "SIGNATURE_VERIFICATION",
														isValid,
														sessionUuid: sessionId,
													},
												});
											};
											return next(
												Page.SignatureSubmitted({
													signature: event.data.signature,
													onClose: closeWithCancelled(copy.Cancelled),
													onApprove: () => sendSignatureVerificationEvent(true),
													onDecline: () =>
														sendSignatureVerificationEvent(false),
												}),
											);
										}
										default:
											logger.debug(
												"refund: unmatched signature verification unhandled variant",
											);
											return never(event.data.state);
									}
								})();
							}
						}
					};
					connection.onMessage(handle(onFrame));
					return onFrame;
				}

				function approved(event: RefundResponse) {
					const { response, getPageProps } = getApprovedResponse({
						event: event.data,
						reference: request.query.reference ?? "",
					});
					done(
						Page.Approved({
							...getPageProps(),
							onDone: () => void resolve(response),
						}),
						"onDone",
					);
					// Response screens ignore the host cancel; done() otherwise restores
					// the default "Cancelled" resolution.
					transport.onCancel(transport.onCancel.ignoreExternal);
				}

				function declined(event: RefundResponse) {
					const response = DeclineResponse_fromRefundPosConnectorResponse({
						data: event.data,
						reference: request.query.reference,
					});
					done(
						Page.Declined({
							...getPageProps({ data: event.data }),
							onCancel: () => void resolve(response),
							onRetry,
						}),
						"onCancel",
					);
					transport.onCancel(transport.onCancel.ignoreExternal);
				}

				function cancel(external = false) {
					/* v8 ignore start -- UI close handlers forward the click event, so `external` is always truthy here; the falsy branch is only reachable via a direct zero-arg call */
					if (!external) {
						logger.info("refund: unmatched internal cancel called");
					}
					/* v8 ignore stop */

					setPage((prev) => {
						return { ...prev, cancelling: true };
					});

					connection.onMessage(
						handle((message) => {
							const event = RefundResponseSchema.safeParse(message).data;
							if (event) {
								return void (() => {
									if (
										!matchesRequestId(event.requestId, {
											status: event.data.status,
										})
									) {
										return;
									}

									connection.send(
										AcknowledgedResponse({
											action: "refund",
											sessionId,
											requestId: requestIdRef.current,
										}),
									);

									if (event.data.status === "APPROVED") {
										return approved(event);
									} else {
										const response =
											DeclineResponse_fromRefundPosConnectorResponse({
												data: event.data,
												reference: request.query.reference,
											});
										return void done(
											Page.Cancelled({
												onClose: () => void resolve(response),
											}),
											"onClose",
											{ skip: true },
										);
									}
								})();
							}
						}),
					);

					connection.send(CancelRequest(sessionId));
					updateTimeout(() => {
						return void done(
							Page.Cancelled({ onClose: closeWithCancelled(copy.Cancelled) }),
							"onClose",
						);
					}, STATE_TRANSACTION_STATE_TIMEOUT);
				}
			}
		}

		return () => {
			// A silence classification in flight must not terminate an unmounted flow.
			done.isLocked = true;
			dispose();
		};
	}, [
		RefundUnmatchedRequest,
		apiClient,
		healthcheck,
		request,
		sessionId,
		setPage,
	]);

	return render(renderedPage);
}

const copy = createOperationCopy("Refund");
const {
	closeWithCancelled,
	closeWithConnectionFailure,
	closeWithInactivityTimeout,
	closeWithNetworkFailure,
	closeWithPairedDeviceBusy,
} = createCloseHandlers({ resolveError: resolve.Error });
